// Freight Cost › Bàn Pricing (Mint-Rates): lưới Hãng × Tuyến đang theo dõi + hàng việc Pricing.
export interface DeskRateRow {
  pol: string;
  pod: string;
  car: string;
  via: string;
  transit: number;
  p20: number;
  p40: number;
  p40hc: number;
  /** 0 = hãng không nhận RF trên tuyến */
  p40rf: number;
  from: number;
  to: number;
  dem: number;
  det: number;
  src: string;
  /** 40HC, 6 kỳ gần nhất (cũ → mới) */
  hist: number[];
}

export interface DeskInquiry {
  id: string;
  route: string;
  carrier: string | null;
  by: string;
  customer: string;
  qty: string;
  /** giờ làm việc còn lại */
  left: number;
}

export type DeskAction = 'ext' | 'ask' | 'enter' | 'skip' | 'route' | 'notify';
export const DESK_ACTION: Record<DeskAction, string> = { ext: 'Gia hạn', ask: 'Hỏi hãng', enter: 'Nhập giá', skip: 'Bỏ qua', route: 'Xem tuyến', notify: 'Báo Sale' };

export interface DeskTask {
  id: string;
  sev: 1 | 2 | 3;
  kind: string;
  title: string;
  /** bản che số tiền cho người không có quyền tiền */
  hidden?: string;
  note: string;
  due: string;
  route: string;
  carrier: string | null;
  acts: DeskAction[];
}

export type CellState = 'ok' | 'soon' | 'exp' | 'none' | 'na';
export const CELL_LABEL: Record<Exclude<CellState, 'na'>, string> = { ok: 'Còn hạn', soon: 'Sắp hết ≤ 7 ngày', exp: 'Hết hạn', none: 'Chưa có giá' };

/** Hôm nay của dữ liệu mẫu (22/Oct); ngày hiệu lực trong `DESK_RATES` tính theo mốc này. */
export const DESK_TODAY = 22;

/** Tên hiển thị của hãng trong lưới (KMTC → KMT). */
export const carLabel = (c: string) => (c === 'KMTC' ? 'KMT' : c);
export const DESK_CARRIERS: readonly string[] = ['YML', 'ONE', 'MSC', 'CMA', 'EMC', 'HMM', 'WHL', 'KMTC'];

export type RegionKey = 'all' | 'US' | 'EU' | 'AS' | 'OT';
export const DESK_REGIONS: readonly { key: RegionKey; label: string }[] = [
  { key: 'all', label: 'Mọi tuyến' },
  { key: 'US', label: 'Mỹ' },
  { key: 'EU', label: 'Âu' },
  { key: 'AS', label: 'Á' },
  { key: 'OT', label: 'Khác' },
];
export function regionOf(route: string): Exclude<RegionKey, 'all'> {
  const pod = route.split(' → ')[1] ?? '';
  return ['LAX', 'LGB', 'NYC'].includes(pod) ? 'US' : ['RTM', 'HAM'].includes(pod) ? 'EU' : ['BUS', 'SIN'].includes(pod) ? 'AS' : 'OT';
}

export const routeOf = (r: Pick<DeskRateRow, 'pol' | 'pod'>) => `${r.pol} → ${r.pod}`;
export const daysLeft = (r: Pick<DeskRateRow, 'to'>) => r.to - DESK_TODAY;

/** Top `n` hãng theo số job 12 tháng. */
export const mainCarriers = (job12: Readonly<Record<string, number>>, n = 8): string[] => Object.keys(job12).sort((a, b) => (job12[b] ?? 0) - (job12[a] ?? 0)).slice(0, n);

export interface DeskCell {
  car: string;
  state: CellState;
  left?: number;
  /** % đổi giá 40HC so kỳ trước */
  delta?: number;
  /** số hỏi giá chờ vào đúng ô */
  inq: number;
}

export function cellOf(rates: readonly DeskRateRow[], serve: Readonly<Record<string, readonly string[]>>, inquiries: readonly DeskInquiry[], route: string, car: string, cargo: 'all' | 'RF'): DeskCell {
  const inq = inquiries.filter((x) => x.route === route && x.carrier === car).length;
  if (!(serve[route] ?? []).includes(car)) return { car, state: 'na', inq: 0 };
  const xs = rates.filter((r) => routeOf(r) === route && r.car === car && (cargo !== 'RF' || r.p40rf > 0));
  if (!xs.length) return { car, state: 'none', inq };
  const cur = [...xs].sort((a, b) => daysLeft(b) - daysLeft(a))[0] as DeskRateRow;
  const left = daysLeft(cur);
  const h = cur.hist;
  const a = h[h.length - 2] as number;
  const b = h[h.length - 1] as number;
  return { car, state: left < 0 ? 'exp' : left <= 7 ? 'soon' : 'ok', left, delta: ((b - a) / a) * 100, inq };
}

export interface DeskRow {
  route: string;
  cells: DeskCell[];
  /** số hãng có giá (còn hạn / sắp hết) */
  nOk: number;
  /** số hãng chạy tuyến trong các cột đang hiện */
  nServe: number;
  inq: number;
  weak: boolean;
}

export function deskRows(rates: readonly DeskRateRow[], serve: Readonly<Record<string, readonly string[]>>, inquiries: readonly DeskInquiry[], opts: { region: RegionKey; cargo: 'all' | 'RF'; cars: readonly string[] }): DeskRow[] {
  return Object.keys(serve)
    .filter((rt) => opts.region === 'all' || regionOf(rt) === opts.region)
    .map((route) => {
      const cells = opts.cars.map((c) => cellOf(rates, serve, inquiries, route, c, opts.cargo));
      const inq = inquiries.filter((x) => x.route === route).length;
      return {
        route,
        cells,
        nOk: cells.filter((c) => c.state === 'ok' || c.state === 'soon').length,
        nServe: (serve[route] ?? []).filter((c) => opts.cars.includes(c)).length,
        inq,
        weak: cells.some((c) => c.state === 'exp' || c.state === 'none') && inq > 0,
      };
    });
}

export function stateCount(rows: readonly DeskRow[], s: CellState): number {
  return rows.reduce((n, r) => n + r.cells.filter((c) => c.state === s).length, 0);
}

export type QueueFilter = 'all' | 'hi' | 'inq';
export const filterTasks = (tasks: readonly DeskTask[], f: QueueFilter): DeskTask[] =>
  tasks.filter((x) => f === 'all' || (f === 'hi' ? x.sev === 3 : x.kind === 'Hỏi giá chờ')).sort((a, b) => b.sev - a.sev);

/** "còn 5 giờ làm việc" → "5h LV"; "còn 2 ngày" → "2 ngày". */
export const shortDue = (due: string) => due.replace(/^còn /, '').replace(/ giờ làm việc$/, 'h LV');

/** Thông báo của Pricing (board `NOTI`): nội dung, mốc giờ, mức (màu chấm). */
export const DESK_NOTIS: readonly { text: string; when: string; tone: 'warn' | 'info' | 'err' }[] = [
  { text: 'Giá ONE CLI → RTM 40HC tăng 40 USD từ 01/Nov', when: '22/Oct/2026 06:12', tone: 'warn' },
  { text: 'YM CERTAINTY 089N trễ 11 giờ theo AIS · 2 job ảnh hưởng', when: '22/Oct/2026 07:40', tone: 'warn' },
  { text: 'TUẤN đề nghị margin riêng cho Gỗ Trường Thành Phát', when: '21/Oct/2026 16:05', tone: 'info' },
  { text: '3 dòng giá KMTC đi SIN hết hiệu lực trong 9 ngày', when: '21/Oct/2026 08:00', tone: 'info' },
  { text: 'Đồng bộ email MSC có 1 dòng lỗi', when: '22/Oct/2026 06:12', tone: 'err' },
];
export type DeskDock = 'q' | 'noti';
